import { useState, useEffect, useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  User,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  Filter,
  Users,
} from "../components/icons";
import { apiClient } from "../lib/api";
import {
  normalizePlayers,
  type PlayerApiResponse,
  buildPlayerPayload,
  computeMissingPlayerFields,
  hasMissingTeamJersey,
  type PlayerMissingField,
} from "../lib/players";
import PlayerCompletionRow from "../components/players/PlayerCompletionRow";
import type {
  PlayerData,
  PlayerPosition,
  PaginatedResponse,
  ImageData,
} from "../types";
import CountrySelect from "../components/CountrySelect";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { usePositionReferenceOptions } from "../hooks/usePositionReferenceOptions";
import { useDuplicateCheck } from "../hooks/useDuplicateCheck";
import { useChangeDetection } from "../hooks/useChangeDetection";
import { useLoading } from "../hooks/useLoading";
import DuplicateWarning from "../components/DuplicateWarning";
import ChangeConfirmation from "../components/ChangeConfirmation";
import SimilarRecordsViewer from "../components/SimilarRecordsViewer";
import { useAuthStore } from "../store/authStore";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import AutocompleteSearch from "../components/AutocompleteSearch";
import ImageUpload from "../components/ImageUpload";
import PlayerAvatar from "../components/PlayerAvatar";
import { useFormValidation } from "../hooks/useFormValidation";
import { playerSchema } from "../lib/validationSchemas";
import {
  applyBackendValidationErrors,
  resolveKnownFieldError,
} from "../lib/backendErrorUtils";

const ADMIN_LOCALES = ["en", "es"] as const;
type AdminLocale = (typeof ADMIN_LOCALES)[number];

const withPlayerFormDefaults = (
  data?: Partial<PlayerData>,
): Partial<PlayerData> => {
  const merged = { ...(data ?? {}) };
  const localizedNames: Record<string, string> = {
    ...(merged.i18n_names ?? {}),
  };

  ADMIN_LOCALES.forEach((locale) => {
    localizedNames[locale] = localizedNames[locale] ?? "";
  });

  return {
    ...merged,
    player_id: merged.player_id ?? "",
    name: merged.name ?? "",
    birth_date: merged.birth_date ?? "",
    country_name: merged.country_name ?? "",
    position: merged.position ?? "CM",
    player_height: merged.player_height ?? undefined,
    player_weight: merged.player_weight ?? undefined,
    i18n_names: localizedNames,
  };
};

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

/** Find a position code's label across every tactical group — used to pass
 *  `SelectValue`'s children explicitly (see the Component API note: Radix
 *  only learns an item's label after it has mounted once). */
function findPositionLabel(
  options: ReturnType<typeof usePositionReferenceOptions>,
  code: string,
): string | undefined {
  for (const group of options.groupOrder) {
    const match = options.grouped[group].find((opt) => opt.code === code);
    if (match) return match.label;
  }
  return undefined;
}

export default function PlayersManager() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const { loading, setLoading } = useLoading(true);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [positionFilter, setPositionFilter] = useState<PlayerPosition | "">("");
  const [provisionalFilter, setProvisionalFilter] = useState<
    "all" | "provisional" | "complete"
  >("all");
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<PlayerData | null>(null);
  // EPIC-95 AC7: which row's inline "complete profile" panel is open, if any.
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [showSimilarRecords, setShowSimilarRecords] = useState(false);
  const [similarRecords, setSimilarRecords] = useState<any[] | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<Partial<PlayerData> | null>(null);
  const { checkDuplicates, duplicates, clearDuplicates } = useDuplicateCheck();
  const { detectChanges, changeResult, duplicateResult, clearChangeResult } =
    useChangeDetection();

  // Form validation
  const {
    validateForm,
    validateAndSetFieldError,
    clearErrors,
    getFieldError,
    setFieldError,
  } = useFormValidation<Partial<PlayerData>>(playerSchema, t);

  const formatDeleteGuardError = (detail: unknown): string | null => {
    if (typeof detail !== "string") return null;
    const match = detail.match(
      /Cannot delete player with (\d+) team assignment(?:s)?/i,
    );
    if (!match) return null;
    const count = Number(match[1]);
    return t("deleteGuards.playerAssignments", { count });
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [formData, setFormData] = useState<Partial<PlayerData>>(() =>
    withPlayerFormDefaults(),
  );

  const positionFilterOptions = usePositionReferenceOptions(positionFilter);
  const positionFormOptions = usePositionReferenceOptions(formData.position);

  // EPIC-95 T4: only reformat + resend `name` when the admin actually
  // changed it — editingItem is undefined on create, so buildPlayerPayload
  // always formats there (no original to compare against).
  const getPlayerPayload = () =>
    buildPlayerPayload(formData, { originalName: editingItem?.name });

  useEffect(() => {
    fetchPlayers();
  }, [currentPage, pageSize, positionFilter, searchTerm]);

  // Monotonic per-invocation id. Guards against a slower, superseded
  // response (e.g. a stale unfiltered page-1 fetch from a StrictMode double
  // mount, or from a request in flight when a search term changes) landing
  // after a newer one and overwriting its result. Incremented synchronously
  // before the first `await` so two invocations fired in the same tick
  // (StrictMode) still get distinct, correctly-ordered ids.
  const fetchPlayersRequestIdRef = useRef(0);

  const fetchPlayers = async () => {
    const requestId = ++fetchPlayersRequestIdRef.current;
    setLoading(true);
    try {
      setError(null);
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      if (positionFilter) {
        params.position = positionFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiClient.get<
        PaginatedResponse<PlayerApiResponse>
      >("/players/", { params });
      if (requestId !== fetchPlayersRequestIdRef.current) return; // stale response, ignore
      setPlayers(normalizePlayers(response.items));
      setTotalItems(response.total);
      setTotalPages(response.total_pages);
    } catch (err: any) {
      if (requestId !== fetchPlayersRequestIdRef.current) return; // stale error, ignore
      setError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      if (requestId === fetchPlayersRequestIdRef.current) {
        setHasInitialLoadCompleted(true);
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(formData)) {
      setError(t("validation.fixErrors"));
      return;
    }

    // EDIT MODE: Always show changes for confirmation
    if (editingItem && editingItem.player_id) {
      // If we haven't shown the change warning yet, detect changes with duplicate check
      if (!showChangeWarning) {
        // Always clear previous result and detect fresh with combined duplicate check
        clearChangeResult();
        clearDuplicates();

        // Detect changes AND check duplicates in single API call
        const backendPayload = getPlayerPayload();
        const result = await detectChanges(
          "players",
          editingItem.player_id,
          backendPayload,
          true,
        );

        // Always show change confirmation, even for small changes
        if (result && result.changes.length > 0) {
          setShowChangeWarning(true);
          return;
        } else if (result && result.changes.length === 0) {
          // No changes detected, just save
          setError(t("validation.noChangesDetected"));
          return;
        }
      }

      // After change confirmation, check if duplicates were already detected
      if (showChangeWarning && changeResult) {
        // Duplicates were already checked in combined call, just show them if found
        if (
          duplicateResult &&
          duplicateResult.has_duplicates &&
          !showSimilarRecords
        ) {
          setShowChangeWarning(false);
          setSimilarRecords(duplicateResult.duplicates ?? []);
          setShowSimilarRecords(true);
          return;
        }
      }
    }

    // CREATE MODE: Check for duplicates
    if (!editingItem) {
      const backendPayload = getPlayerPayload();
      const result = await checkDuplicates("players", backendPayload);

      // Check for 100% duplicates (should be rejected automatically)
      const exactDuplicate = result.duplicates?.find(
        (d) => d.similarity_score === 1.0 || d.match_score === 100,
      );

      if (exactDuplicate) {
        setError(t("validation.exactDuplicateExists"));
        return;
      }

      // Check for similar duplicates (80%+)
      if (result.has_duplicates && !showDuplicateWarning) {
        setShowDuplicateWarning(true);
        return;
      }
    }

    await savePlayer();
  };

  const savePlayer = async () => {
    try {
      const payload = getPlayerPayload();
      if (editingItem) {
        await apiClient.put(`/players/${editingItem.player_id}`, payload);
      } else {
        await apiClient.post("/players/", payload);
      }
      await fetchPlayers();
      handleCloseForm();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const handled = applyBackendValidationErrors(detail, {
        setFieldError,
        clearErrors,
        translate: t,
      });
      if (handled) {
        setError(t("validation.fixErrors"));
        return;
      }

      const knownFieldError = resolveKnownFieldError(detail);
      if (knownFieldError) {
        setFieldError(knownFieldError.field, t(knownFieldError.translationKey));
        setError(t("validation.fixErrors"));
        return;
      }

      if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("errorSavingData"));
      }
    }
  };

  const handleContinueWithDuplicates = async () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
    await savePlayer();
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
  };

  const handleConfirmChanges = async () => {
    // User confirmed the changes, proceed to next step (duplicate check)
    setShowChangeWarning(false);
    // Don't clear changeResult here, we need it for the duplicate check

    // Continue with the duplicate check and save flow
    await handleSubmit(new Event("submit") as any);
  };

  const handleCancelChanges = () => {
    // User canceled, reset everything
    setShowChangeWarning(false);
    clearChangeResult();
    setError(null);

    // Revert to original data
    if (originalFormData) {
      setFormData(originalFormData);
    }
  };

  const handleCloseSimilarRecords = () => {
    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();
  };

  const handleOpenSimilarRecord = (record: any) => {
    const normalizedPlayer = normalizePlayers([record])[0];
    const formValue = withPlayerFormDefaults(normalizedPlayer);

    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();

    setEditingItem(normalizedPlayer);
    setFormData(formValue);
    setOriginalFormData(formValue);
    setShowForm(true);
  };

  const handleLocalizedNameChange = (locale: AdminLocale, value: string) => {
    setFormData((prev) => ({
      ...prev,
      i18n_names: {
        ...(prev.i18n_names ?? {}),
        [locale]: value,
      },
    }));
    validateAndSetFieldError(`i18n_names.${locale}`, value);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await apiClient.delete(`/players/${id}`);
      await fetchPlayers();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const guardMessage = formatDeleteGuardError(detail);
      if (guardMessage) {
        setError(guardMessage);
        return;
      }
      setError(typeof detail === "string" ? detail : t("errorDeletingData"));
    }
  };

  const handleEdit = (item: PlayerData) => {
    const formValue = withPlayerFormDefaults(item);
    setEditingItem(item);
    setFormData(formValue);
    setOriginalFormData(formValue); // Store original data for comparison
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setShowDuplicateWarning(false);
    setShowChangeWarning(false);
    setShowSimilarRecords(false);
    setOriginalFormData(null);
    clearDuplicates();
    clearChangeResult();
    clearErrors();
    setError(null);
    setFormData(withPlayerFormDefaults());
  };

  // Fetch player suggestions for autocomplete
  const fetchPlayerSuggestions = async (
    query: string,
  ): Promise<PlayerApiResponse[]> => {
    try {
      const response = await apiClient.get<PlayerApiResponse[]>(
        "/players/search/suggestions",
        {
          params: { q: query },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching player suggestions:", error);
      return [];
    }
  };

  // Handle selecting an existing player from autocomplete
  const handleSelectExistingPlayer = (playerData: PlayerApiResponse) => {
    const normalizedPlayer = normalizePlayers([playerData])[0];
    const formValue = withPlayerFormDefaults(normalizedPlayer);
    setEditingItem(normalizedPlayer);
    setFormData(formValue);
    setOriginalFormData(formValue);
  };

  const filteredPlayers = players.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.country_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesPosition = !positionFilter || item.position === positionFilter;
    const matchesProvisional =
      provisionalFilter === "all" ||
      (provisionalFilter === "provisional"
        ? Boolean(item.is_provisional)
        : !item.is_provisional);
    return matchesSearch && matchesPosition && matchesProvisional;
  });

  const getPositionColor = (position: string) => {
    // Goalkeeper
    if (position === "GK") return "bg-purple-100 text-purple-800";
    // Defenders
    if (["CB", "LB", "RB", "LWB", "RWB", "SW"].includes(position)) {
      return "bg-blue-100 text-blue-800";
    }
    // Midfielders
    if (["CDM", "CM", "CAM", "LM", "RM", "LW", "RW"].includes(position)) {
      return "bg-green-100 text-green-800";
    }
    // Forwards
    if (["CF", "ST", "LF", "RF", "SS"].includes(position)) {
      return "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  if (loading && !hasInitialLoadCompleted) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <User className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t("players")}</h1>
        </div>
        {user?.role === "admin" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/roster-import")}
              className="btn btn-secondary flex items-center space-x-2"
              data-testid="players-open-roster-import"
            >
              <Users className="h-5 w-5" />
              <span>{t("rosterImport.title")}</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>{t("createPlayer")}</span>
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="card grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t("search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Select
            value={positionFilter === "" ? UNSET : positionFilter}
            onValueChange={(next) =>
              setPositionFilter(
                (next === UNSET ? "" : next) as PlayerPosition | "",
              )
            }
          >
            <SelectTrigger
              aria-label={t("filterByPosition")}
              className="pl-10 w-full"
              data-testid="position-filter-select"
            >
              <SelectValue placeholder={t("allPositions")}>
                {positionFilter === ""
                  ? undefined
                  : findPositionLabel(positionFilterOptions, positionFilter)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={UNSET}
                data-testid="position-filter-option-unset"
              >
                {t("allPositions")}
              </SelectItem>
              {positionFilterOptions.groupOrder.map((group) =>
                positionFilterOptions.grouped[group].length === 0 ? null : (
                  <SelectGroup key={group}>
                    <SelectLabel>
                      {t(`referenceData.positionGroups.${group}`)}
                    </SelectLabel>
                    {positionFilterOptions.grouped[group].map((opt) => (
                      <SelectItem
                        key={opt.code}
                        value={opt.code}
                        data-testid={`position-filter-option-${opt.code}`}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Select
            value={provisionalFilter}
            onValueChange={(next) =>
              setProvisionalFilter(next as "all" | "provisional" | "complete")
            }
          >
            <SelectTrigger
              aria-label={t("quickCreatePlayer.provisionalFilterLabel")}
              className="w-full"
              data-testid="players-provisional-filter"
            >
              <SelectValue>
                {provisionalFilter === "all"
                  ? t("quickCreatePlayer.provisionalFilterAll")
                  : provisionalFilter === "provisional"
                    ? t("quickCreatePlayer.provisionalFilterOnly")
                    : t("quickCreatePlayer.provisionalFilterComplete")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value="all"
                data-testid="players-provisional-filter-option-all"
              >
                {t("quickCreatePlayer.provisionalFilterAll")}
              </SelectItem>
              <SelectItem
                value="provisional"
                data-testid="players-provisional-filter-option-provisional"
              >
                {t("quickCreatePlayer.provisionalFilterOnly")}
              </SelectItem>
              <SelectItem
                value="complete"
                data-testid="players-provisional-filter-option-complete"
              >
                {t("quickCreatePlayer.provisionalFilterComplete")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="card">
        {filteredPlayers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || positionFilter || provisionalFilter !== "all"
                ? t("noData")
                : t("noPlayers")}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y divide-gray-200"
                aria-label={t("players")}
              >
                <thead className="table-header">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-10"
                    >
                      {t("photo", "Photo")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("name")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("localizedNames")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("position")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("country")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("birthDate")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("age")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("height")}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {t("weight")}
                    </th>
                    {user?.role === "admin" && (
                      <th
                        scope="col"
                        className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("actions")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="data-rows">
                  {filteredPlayers.map((item) => {
                    // EPIC-95 AC7: independent of is_provisional (a
                    // roster-imported row can land with a blank position,
                    // which is_provisional's own backend rule never checked
                    // at create time — see computeMissingPlayerFields).
                    const missingFields = computeMissingPlayerFields(item);
                    // EPIC-95 judge round 1, point 3: a player can be
                    // complete on every field above and still have an
                    // active team link with no jersey number — a SEPARATE
                    // gap that never affects is_provisional (the amended
                    // spec's Completeness paragraph), so it's checked and
                    // shown independently rather than folded into
                    // missingFields.
                    const missingJerseyTeams = item.missing_jersey_teams ?? [];
                    const hasJerseyGap = hasMissingTeamJersey(item);
                    const hasAnyMissingField =
                      missingFields.length > 0 || hasJerseyGap;
                    const isExpanded = expandedPlayerId === item.player_id;
                    const columnCount = user?.role === "admin" ? 10 : 9;
                    return (
                      <Fragment key={item._id}>
                        <tr className="">
                          <td className="px-4 py-3">
                            <PlayerAvatar player={item} size={28} />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            <span className="flex items-center gap-2 flex-wrap">
                              {item.name}
                              {item.is_provisional && (
                                <span
                                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800"
                                  data-testid={`provisional-badge-${item.player_id}`}
                                  title={t(
                                    "quickCreatePlayer.provisionalBadgeTooltip",
                                  )}
                                >
                                  {t("quickCreatePlayer.provisionalBadge")}
                                </span>
                              )}
                            </span>
                            {hasAnyMissingField && (
                              <div
                                className="mt-1 flex items-center gap-2 flex-wrap"
                                data-testid={`missing-fields-${item.player_id}`}
                              >
                                <span className="text-xs text-amber-700">
                                  {[
                                    missingFields.length > 0
                                      ? t("missingFields.label", {
                                          fields: missingFields
                                            .map((field: PlayerMissingField) =>
                                              t(
                                                `missingFields.fieldNames.${field}`,
                                              ),
                                            )
                                            .join(", "),
                                        })
                                      : null,
                                    hasJerseyGap
                                      ? t("missingFields.jerseyLabel", {
                                          teams: missingJerseyTeams
                                            .map((team) => team.team_name)
                                            .join(", "),
                                        })
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                                {user?.role === "admin" && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedPlayerId(
                                        isExpanded ? null : item.player_id,
                                      )
                                    }
                                    data-testid={`complete-profile-toggle-${item.player_id}`}
                                    className="text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                                  >
                                    {isExpanded
                                      ? t("cancel")
                                      : t("missingFields.complete")}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            <div className="space-y-1">
                              {ADMIN_LOCALES.map((locale) => (
                                <div
                                  key={locale}
                                  className="flex items-center text-xs"
                                >
                                  <span className="font-semibold uppercase text-gray-500 mr-2">
                                    {locale}
                                  </span>
                                  <span>
                                    {item.i18n_names?.[locale] || "-"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPositionColor(
                                item.position,
                              )}`}
                            >
                              {positionFormOptions.labelFor(item.position)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.country_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.birth_date}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.age || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.player_height
                              ? `${item.player_height} cm`
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.player_weight
                              ? `${item.player_weight} kg`
                              : "-"}
                          </td>
                          {user?.role === "admin" && (
                            <td className="px-4 py-3 text-right text-sm font-medium space-x-1">
                              <button
                                onClick={() => handleEdit(item)}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                                title={t("edit")}
                                aria-label={`${t("edit")} ${item.name}`}
                              >
                                <Edit className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.player_id)}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
                                title={t("delete")}
                                aria-label={`${t("delete")} ${item.name}`}
                              >
                                <Trash2
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </button>
                            </td>
                          )}
                        </tr>
                        {isExpanded && (
                          <PlayerCompletionRow
                            key={`${item._id}-completion`}
                            player={item}
                            missingFields={missingFields}
                            colSpan={columnCount}
                            onCancel={() => setExpandedPlayerId(null)}
                            onSaved={async () => {
                              setExpandedPlayerId(null);
                              await fetchPlayers();
                            }}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="player-form-modal-title"
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8"
          >
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 bg-white z-10 rounded-t-xl">
              <h2
                id="player-form-modal-title"
                className="text-xl font-semibold text-gray-900"
              >
                {editingItem ? t("edit") : t("createPlayer")}
              </h2>
              <button
                onClick={handleCloseForm}
                aria-label={t("cancel")}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 transition-colors"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <form
              id="player-form"
              onSubmit={handleSubmit}
              className="p-6 space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto"
            >
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              {showDuplicateWarning &&
                duplicates &&
                duplicates.has_duplicates && (
                  <DuplicateWarning
                    duplicates={duplicates.duplicates}
                    onContinue={handleContinueWithDuplicates}
                    onCancel={handleCancelDuplicates}
                    entityType="player"
                    onViewSimilarRecords={
                      duplicates.duplicates.length > 0
                        ? () => {
                            setSimilarRecords(duplicates.duplicates);
                            setShowSimilarRecords(true);
                          }
                        : undefined
                    }
                  />
                )}
              {showChangeWarning &&
                changeResult &&
                changeResult.changes.length > 0 && (
                  <ChangeConfirmation
                    changePercentage={changeResult.change_percentage}
                    changes={changeResult.changes}
                    onConfirm={handleConfirmChanges}
                    onCancel={handleCancelChanges}
                  />
                )}
              <div className="flex items-start gap-4 mb-4">
                <ImageUpload
                  kind="player-photo"
                  entityId={editingItem?.player_id}
                  url={formData.photo_url}
                  legacyImage={
                    formData.photo_image as ImageData | null | undefined
                  }
                  onUploaded={(photoUrl) =>
                    setFormData({ ...formData, photo_url: photoUrl })
                  }
                  onDeleted={() =>
                    setFormData({ ...formData, photo_url: undefined })
                  }
                  canEdit={user?.role === "admin"}
                  label={t("photo", "Photo")}
                  size={80}
                />
                <div className="flex-1 space-y-4">
                  <div>
                    <label
                      htmlFor="player-id"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("playerId")}
                    </label>
                    <input
                      id="player-id"
                      type="text"
                      maxLength={40}
                      value={formData.player_id || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, player_id: e.target.value });
                        validateAndSetFieldError("player_id", e.target.value);
                      }}
                      onBlur={(e) =>
                        validateAndSetFieldError("player_id", e.target.value)
                      }
                      className={`input w-full ${
                        getFieldError("player_id") ? "border-red-500" : ""
                      }`}
                      placeholder="player_123"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {t("playerIdHelper")}
                    </p>
                    {getFieldError("player_id") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("player_id")}
                      </p>
                    )}
                  </div>
                  <div>
                    <AutocompleteSearch<PlayerApiResponse>
                      name="name"
                      value={formData.name || ""}
                      onChange={(value) => {
                        setFormData({ ...formData, name: value });
                        validateAndSetFieldError("name", value);
                      }}
                      onSelectSuggestion={handleSelectExistingPlayer}
                      fetchSuggestions={fetchPlayerSuggestions}
                      getDisplayText={(player) => player.name}
                      getSecondaryText={(player) =>
                        `${
                          player.country_name ||
                          player.nationality ||
                          t("unknownCountry")
                        } • ${player.position} • ${player.birth_date}`
                      }
                      placeholder={t("playerNamePlaceholder")}
                      label={t("name")}
                      required
                      minChars={3}
                    />
                    {getFieldError("name") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("name")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {t("localizedNames")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("localizedNamesDescription")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ADMIN_LOCALES.map((locale) => {
                    const fieldKey = `i18n_names.${locale}`;
                    const inputId = `player-i18n-name-${locale}`;
                    return (
                      <div key={locale}>
                        <label
                          htmlFor={inputId}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {locale === "en"
                            ? t("localizedNameEn")
                            : t("localizedNameEs")}
                        </label>
                        <input
                          id={inputId}
                          type="text"
                          maxLength={100}
                          value={formData.i18n_names?.[locale] ?? ""}
                          onChange={(e) =>
                            handleLocalizedNameChange(locale, e.target.value)
                          }
                          onBlur={(e) =>
                            validateAndSetFieldError(fieldKey, e.target.value)
                          }
                          className={`input w-full ${
                            getFieldError(fieldKey) ? "border-red-500" : ""
                          }`}
                        />
                        {getFieldError(fieldKey) && (
                          <p className="mt-1 text-sm text-red-600">
                            {getFieldError(fieldKey)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="player-birth-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("birthDate")} *
                  </label>
                  <input
                    id="player-birth-date"
                    type="date"
                    required
                    value={formData.birth_date}
                    onChange={(e) => {
                      setFormData({ ...formData, birth_date: e.target.value });
                      validateAndSetFieldError("birth_date", e.target.value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError("birth_date", e.target.value)
                    }
                    className={`input w-full ${
                      getFieldError("birth_date") ? "border-red-500" : ""
                    }`}
                  />
                  {getFieldError("birth_date") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("birth_date")}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="player-country"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("country")} *
                  </label>
                  <CountrySelect
                    id="player-country"
                    required
                    value={formData.country_name || ""}
                    onChange={(next) => {
                      setFormData({
                        ...formData,
                        country_name: next,
                      });
                      validateAndSetFieldError("country_name", next);
                    }}
                    onBlur={() =>
                      validateAndSetFieldError(
                        "country_name",
                        formData.country_name || "",
                      )
                    }
                    placeholder={t("selectCountry")}
                    className={
                      getFieldError("country_name") ? "border-red-500" : ""
                    }
                  />
                  {getFieldError("country_name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("country_name")}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="player-position"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("position")} *
                  </label>
                  <Select
                    value={formData.position}
                    onValueChange={(next) => {
                      const value = next as PlayerPosition;
                      setFormData({ ...formData, position: value });
                      validateAndSetFieldError("position", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        validateAndSetFieldError(
                          "position",
                          formData.position ?? "",
                        );
                      }
                    }}
                    required
                  >
                    <SelectTrigger
                      id="player-position"
                      className={
                        getFieldError("position") ? "border-red-500" : ""
                      }
                    >
                      <SelectValue>
                        {(() => {
                          const label = findPositionLabel(
                            positionFormOptions,
                            formData.position ?? "",
                          );
                          if (!label) return undefined;
                          const opt = positionFormOptions.groupOrder
                            .flatMap((g) => positionFormOptions.grouped[g])
                            .find((o) => o.code === formData.position);
                          return opt?.isDeprecated
                            ? t("referenceData.deprecatedOption", {
                                label,
                                defaultValue: `${label} (deprecated)`,
                              })
                            : label;
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {positionFormOptions.groupOrder.map((group) =>
                        positionFormOptions.grouped[group].length ===
                        0 ? null : (
                          <SelectGroup key={group}>
                            <SelectLabel>
                              {t(`referenceData.positionGroups.${group}`)}
                            </SelectLabel>
                            {positionFormOptions.grouped[group].map((opt) => (
                              <SelectItem
                                key={opt.code}
                                value={opt.code}
                                deprecated={opt.isDeprecated}
                                data-testid={`player-position-option-${opt.code}`}
                              >
                                {opt.isDeprecated
                                  ? t("referenceData.deprecatedOption", {
                                      label: opt.label,
                                      defaultValue: `${opt.label} (deprecated)`,
                                    })
                                  : opt.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  {getFieldError("position") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("position")}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="player-height"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("height")} (cm)
                  </label>
                  <input
                    id="player-height"
                    type="number"
                    min="0"
                    value={formData.player_height ?? ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      setFormData({ ...formData, player_height: value });
                      validateAndSetFieldError("player_height", value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError(
                        "player_height",
                        e.target.value ? parseFloat(e.target.value) : undefined,
                      )
                    }
                    className={`input w-full ${
                      getFieldError("player_height") ? "border-red-500" : ""
                    }`}
                  />
                  {getFieldError("player_height") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("player_height")}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="player-weight"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("weight")} (kg)
                  </label>
                  <input
                    id="player-weight"
                    type="number"
                    min="0"
                    value={formData.player_weight ?? ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      setFormData({ ...formData, player_weight: value });
                      validateAndSetFieldError("player_weight", value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError(
                        "player_weight",
                        e.target.value ? parseFloat(e.target.value) : undefined,
                      )
                    }
                    className={`input w-full ${
                      getFieldError("player_weight") ? "border-red-500" : ""
                    }`}
                  />
                  {getFieldError("player_weight") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("player_weight")}
                    </p>
                  )}
                </div>
              </div>
            </form>
            <div className="flex justify-end space-x-3 p-6 border-t sticky bottom-0 bg-white z-10 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseForm}
                className="btn btn-secondary"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                form="player-form"
                disabled={showChangeWarning || showSimilarRecords}
                className={`btn btn-primary ${
                  showChangeWarning || showSimilarRecords
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Similar Records Viewer Modal */}
      {showSimilarRecords && similarRecords && similarRecords.length > 0 && (
        <SimilarRecordsViewer
          records={similarRecords}
          currentData={formData}
          entityType="player"
          onClose={handleCloseSimilarRecords}
          onOpenRecord={handleOpenSimilarRecord}
        />
      )}
    </div>
  );
}
